import { Grid, Paper } from "@mui/material";
import { compose } from "@reduxjs/toolkit";
import React from "react";
import { connect } from "react-redux";
import swal from "sweetalert";
import { ButtonCompo } from "../../components/Comman/Button";
import DynamicDetails from "../../components/dynamicscreens/DynamicDetails";
import DynamicFormWithoutValidation from "../../components/dynamicscreens/DynamicFormWithoutValidation";
import { withRouter } from "../../components/withRouter";
import endpoint from "../../config/endpoints";
import {
  noInternetMsg,
  saveFailedMsg,
  savemsg,
  saveWarningMsg,
  serverMsg,
} from "../../config/messageconstant";
import { mandatoryCheck } from "../../config/ValidationCheck";
import { stockHolderTargetJson } from "../../DynamicFormsJson/Transaction/stockholderTarget";
import { getAreaByTalukaAndYear } from "../../Slice/area.slice";
import { getNotAssignBeat } from "../../Slice/beatTarget.slice";
import { getDistrictByZone } from "../../Slice/district.slice";
import { getRegionNew } from "../../Slice/region.slice";
import { getMonthsFromYearId } from "../../Slice/regionAndTarget.slice";
import { getStateByRegion } from "../../Slice/state.slice";
import { getTalukaByDistrict } from "../../Slice/taluka.slice";
import { getTargetByStakeHolderTypeIdAndYearId } from "../../Slice/target.slice";
import { getActiveYear } from "../../Slice/year.slice";
import { getZoneByState } from "../../Slice/zone.slice";
import { apiPost } from "../../utils/api_service";
import { showLoador, showNotification } from "../Landing/Landing.slice";

// ── Sub-components ─────────────────────────────────────────────────────────────
import MatrixTable from "./components/MatrixTable";

// ─────────────────────────────────────────────────────────────────────────────
// Pure helpers (no side-effects — easy to unit-test)
// ─────────────────────────────────────────────────────────────────────────────

/** Maps conversion-factor type key → label text */
const getConversionLabel = (type) => {
  if (type === "0") return "K";
  if (type === "1") return "L";
  if (type === "2") return "CR";
  return "";
};

/** Maps conversion-factor type key → numeric multiplier */
const getConversionMultiplier = (type) => {
  if (type === "0") return 1000;
  if (type === "1") return 100000;
  if (type === "2") return 10000000;
  return 1;
};

/**
 * Builds the initial matrix from beats × months.
 * Each cell starts with target = 0.
 */
const buildMatrix = (beatList, yearNameList) =>
  beatList.map((beat) => ({
    ...beat,
    beatListData: yearNameList.map((month) => ({ ...month, target: 0 })),
    totalTarget: 0,
  }));

/**
 * Derives the month key used when POSTing.
 * Trims full month names that the API expects as short codes.
 */
const toMonthKey = (monthName) => {
  const full = ["March", "April", "July"];
  return full.includes(monthName)
    ? monthName.toLowerCase()
    : monthName.substring(0, 3).toLowerCase();
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

class StockHolderTarget extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      dynamicMasterData: {
        regionDetail: this.props.regionList?.region,
        stateDetail: this.props.stateList?.stateByRegion,
        zoneDetail: this.props.zoneList?.zoneByState,
        districtDetail: this.props.districtList?.districtByZone,
        talukaDetail: this.props.talukaList?.talukaByDistrict,
        areaDetail: [],
        yearDetail: this.props.yearList?.activeYear,
      },
      yearNameList: [],
      conversionFactorText: "K",           // default type "0" → "K"
      fieldData: { type: "0" },
      formErrors: {},
      matrixData: [],
      yearId: "",
      totalOftarget: 0,
      isLog: true,
      moduleObjDataObj: {},
    };
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  async componentDidMount() {
    const { getRegionNew, getActiveYear, showLoador, showNotification } = this.props;

    if (!navigator.onLine) {
      showNotification({ msg: noInternetMsg, severity: "error" });
      return;
    }

    const moduleObjData = localStorage.getItem("moduleObj");
    this.setState({
      moduleObjDataObj: moduleObjData ? JSON.parse(moduleObjData) : {},
    });

    showLoador({ loador: true });
    await getRegionNew().then(({ success }) => {
      showLoador({ loador: false });
      if (!success) showNotification({ msg: serverMsg, severity: "error" });
    });

    showLoador({ loador: true });
    await getActiveYear().then(({ success }) => {
      showLoador({ loador: false });
      if (!success) showNotification({ msg: serverMsg, severity: "error" });
    });
  }

  componentWillReceiveProps(nextProps) {
    // Sync Redux-driven dropdown lists into local dynamicMasterData
    const sliceMap = [
      { prop: "regionList", key: "region", stateKey: "regionDetail" },
      { prop: "stateList", key: "stateByRegion", stateKey: "stateDetail" },
      { prop: "zoneList", key: "zoneByState", stateKey: "zoneDetail" },
      { prop: "districtList", key: "districtByZone", stateKey: "districtDetail" },
      { prop: "talukaList", key: "talukaByDistrict", stateKey: "talukaDetail" },
      { prop: "yearList", key: "activeYear", stateKey: "yearDetail" },
    ];

    const updates = {};
    sliceMap.forEach(({ prop, key, stateKey }) => {
      if (this.props[prop]?.[key] !== nextProps[prop]?.[key]) {
        updates[stateKey] = nextProps[prop]?.[key];
      }
    });

    if (Object.keys(updates).length) {
      this.setState((prev) => ({
        dynamicMasterData: { ...prev.dynamicMasterData, ...updates },
      }));
    }
  }

  // ── Validation ─────────────────────────────────────────────────────────────

  checkValidationOnSubmit = () => {
    const { fieldData } = this.state;
    const { formErrors, formValid } = mandatoryCheck({
      fieldData,
      fieldMeta: stockHolderTargetJson.fieldMeta,
    });
    this.setState({ formErrors });
    return formValid;
  };

  // ── Cascading dropdowns ────────────────────────────────────────────────────

  getListById = async (data) => {
    const {
      getStateByRegion,
      getZoneByState,
      getDistrictByZone,
      getTalukaByDistrict,
      getAreaByTalukaAndYear,
      showLoador,
      showNotification,
    } = this.props;

    if (!navigator.onLine) {
      showNotification({ msg: noInternetMsg, severity: "error" });
      return;
    }

    if (data.name) this.setState({ yearId: data.name });

    showLoador({ loador: true });

    if (data.region?.id) {
      await getStateByRegion({ regionIds: data.region.id }).then(({ success }) => {
        if (!success) showNotification({ msg: serverMsg, severity: "error" });
      });
    }
    if (data.state) {
      await getZoneByState({ stateIds: data.state }).then(({ success }) => {
        if (!success) showNotification({ msg: serverMsg, severity: "error" });
      });
    }
    if (data.zone) {
      await getDistrictByZone({ zoneIds: data.zone }).then(({ success }) => {
        if (!success) showNotification({ msg: serverMsg, severity: "error" });
      });
    }
    if (data.district) {
      await getTalukaByDistrict({ districtIds: data.district }).then(({ success }) => {
        if (!success) showNotification({ msg: serverMsg, severity: "error" });
      });
    }
    if (data.taluka && data.taluka !== "") {
      const dataToSend = {
        talukaIds:
          typeof data.taluka === "string"
            ? data.taluka.split(",")
            : data.taluka,
        yearId: this.state.yearId,
      };
      await getAreaByTalukaAndYear({ dataToSend }).then(({ response, success }) => {
        if (!success) {
          showNotification({ msg: serverMsg, severity: "error" });
        } else {
          this.setState((prev) => ({
            dynamicMasterData: { ...prev.dynamicMasterData, areaDetail: response },
          }));
        }
      });
    }

    showLoador({ loador: false });
  };

  // ── Search / Load matrix ───────────────────────────────────────────────────

  onSearchList = async () => {
    const { getMonthsFromYearId, getNotAssignBeat, showLoador, showNotification } = this.props;
    const { fieldData } = this.state;

    if (!navigator.onLine) {
      showNotification({ msg: noInternetMsg, severity: "error" });
      return;
    }
    if (!this.checkValidationOnSubmit()) return;

    this.setState({ conversionFactorText: getConversionLabel(fieldData.type) });

    showLoador({ loador: true });

    const yearNameList = await getMonthsFromYearId({ yearId: fieldData.name }).then(
      ({ response, success }) => {
        if (!success) {
          showNotification({ msg: serverMsg, severity: "error" });
          return [];
        }
        return response;
      }
    );

    this.setState({ yearNameList, yearId: fieldData.name });

    if (fieldData.area && fieldData.area !== "" && yearNameList.length) {
      const beatList = await getNotAssignBeat({
        yearId: fieldData.name,
        areaId: fieldData.area,
      }).then(({ success, response }) => {
        if (!success) {
          showNotification({ msg: serverMsg, severity: "error" });
          return [];
        }
        return response;
      });

      this.setState({ matrixData: buildMatrix(beatList, yearNameList) });
    }

    showLoador({ loador: false });
  };

  // ── Cell value change (unchanged logic) ───────────────────────────────────

  onTextFieldDataChange = (monthId, beatId) => (event) => {
    const { value } = event.target;
    const { matrixData } = this.state;
    let totalOftarget = 0;

    const updatedMatrixData = matrixData.map((rowD) => {
      if (rowD.id !== beatId) return rowD;

      let totalTarget = 0;
      const updatedBeatListData = rowD.beatListData.map((monthData) => {
        const updated =
          monthData.name === monthId ? { ...monthData, target: value } : monthData;
        totalTarget += +updated.target;
        return updated;
      });
      return { ...rowD, beatListData: updatedBeatListData, totalTarget };
    });

    updatedMatrixData.forEach((rowD) => {
      totalOftarget += rowD.totalTarget;
    });

    this.setState({ matrixData: updatedMatrixData, totalOftarget });
  };

  // ── Field change + cascading reset ────────────────────────────────────────

  onDataChange = (fieldName, newValue) => {
    const { fieldData } = this.state;
    const dataToSearch = {};
    const dataToReset = { ...fieldData };
    let listError = false;

    stockHolderTargetJson.fieldMeta.forEach((currentField) => {
      if (currentField.getDate && currentField.dataKey === fieldName) {
        dataToReset[currentField.getDate] = newValue.add(365, "day");
      }
      if (currentField.dataKey === fieldName && currentField.getListId) {
        currentField.getListId.split(",").forEach((key) => {
          dataToSearch[key] = key === fieldName ? newValue : fieldData[key];
        });
        this.getListById(dataToSearch);
      }
      if (currentField.getListFrom) {
        currentField.getListFrom.split(",").forEach((key) => {
          if (key === fieldName && dataToReset[key]) {
            delete dataToReset[currentField.dataKey];
          }
        });
      }
      if (
        !listError &&
        fieldData[currentField.dataKey] &&
        currentField.controlType !== "datepicker" &&
        currentField.getListFrom == null
      ) {
        dataToReset[currentField.dataKey] = fieldData[currentField.dataKey];
      }
    });

    dataToReset[fieldName] = newValue;
    this.setState({
      fieldData: dataToReset,
      conversionFactorText: getConversionLabel(newValue),
    });
  };

  // ── Submit / Save (unchanged logic) ───────────────────────────────────────

  onSubmit = () => {
    const { matrixData, yearId, fieldData } = this.state;
    const multiplier = getConversionMultiplier(fieldData.type);

    const saveTargetList = matrixData.map((row) => {
      const base = {
        beat: { id: row.id },
        yearMaster: { id: yearId },
        totalTarget: +row.totalTarget * multiplier,
      };
      row.beatListData.forEach((beat) => {
        base[toMonthKey(beat.month)] = +beat.target * multiplier;
      });
      return base;
    });

    swal({
      title: "Are you sure?",
      text: saveWarningMsg,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) this.onSave(saveTargetList);
    });
  };

  onSave = (saveTargetList) => {
    const { showLoador, showNotification } = this.props;
    const { isLog, moduleObjDataObj, yearId } = this.state;

    if (!navigator.onLine) {
      showNotification({ msg: noInternetMsg, severity: "error" });
      return;
    }

    const beatIds = saveTargetList.map((item) => item.beat?.id);

    showLoador({ loador: true });
    apiPost({
      url: `${endpoint.beatTarget}/save-list`,
      postBody: saveTargetList,
    }).then(({ data, success }) => {
      showLoador({ loador: false });

      if (!success) {
        showNotification({ msg: saveFailedMsg, severity: "error" });
        return;
      }

      // Activity log
      if (isLog && data.data !== null) {
        const dataToSaveForMLog = {
          module: { moduleId: moduleObjDataObj.moduleId },
          subModule: { subModuleId: moduleObjDataObj.subModuleId },
          operationType: 0,
          type: 1,
          operationPerformId: yearId,
          subOperationPerformIds: beatIds.join(", "),
          updatedJson: JSON.stringify(data.data),
        };

        showLoador({ loador: true });
        apiPost({ url: endpoint.mlog, postBody: dataToSaveForMLog }).then(
          ({ success: logSuccess }) => {
            showLoador({ loador: false });
            if (!logSuccess) {
              showNotification({ msg: saveFailedMsg, severity: "error" });
            }
          }
        );
      }

      showNotification({ msg: savemsg });
      this.props.navigate(-1);
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  render() {
    const {
      yearNameList,
      matrixData,
      dynamicMasterData,
      totalOftarget,
      conversionFactorText,
      fieldData,
      formErrors,
    } = this.state;

    return (
      <Paper sx={{ padding: 2, borderRadius: 2 }} elevation={0}>
        <br />

        {/* ── Filter form ── */}
        <DynamicFormWithoutValidation
          showSaveBtnMain={false}
          getListById={this.getListById}
          showBackToList={true}
          dynamicMasterData={dynamicMasterData}
          screenTitle={stockHolderTargetJson.screenTitle}
          fieldMeta={stockHolderTargetJson.fieldMeta}
          apiBaseURL={stockHolderTargetJson.apiBaseURL}
          tableColumns={[]}
          tableData={[]}
          onDataChange={this.onDataChange}
          saveBtnText="Search"
          onSave={this.onSearchList}
          padding={true}
          paddingTop={true}
          fieldData={fieldData}
          formErrors={formErrors}
        />

        <br />

        {/* ── Search button ── */}
        <Grid container spacing={1}>
          <Grid
            item xs={12} sm={12} md={12} lg={12}
            container justifyContent="right"
            rowSpacing={2}
            columnSpacing={{ xs: 12, sm: 6, md: 1 }}
          >
            <ButtonCompo
              size="medium"
              type="submit"
              variant="contained"
              name="Search"
              onClick={this.onSearchList}
            />
          </Grid>
        </Grid>

        <br />
        {/* ── Beat × Month matrix (native table, memoized rows) ── */}

        {matrixData.length !== 0 && (
          <>
            <MatrixTable
              matrixData={matrixData}
              yearNameList={yearNameList}
              onTextFieldDataChange={this.onTextFieldDataChange}
            />

            <br />

            {/* ── Grand total ── */}
            <DynamicDetails
              DataToSet={[
                {
                  xs: 12, sm: 6, md: 6, lg: 6,
                  label: "Total target",
                  value: `${totalOftarget}${conversionFactorText}`,
                },
              ]}
            />

            {/* ── Submit button ── */}
            <Grid container spacing={1}>
              <Grid
                item xs={12} sm={12} md={12} lg={12}
                container justifyContent="right"
                rowSpacing={2}
                columnSpacing={{ xs: 12, sm: 6, md: 1 }}
              >
                <ButtonCompo
                  size="medium"
                  type="submit"
                  variant="contained"
                  name="Submit"
                  onClick={this.onSubmit}
                />
              </Grid>
            </Grid>
          </>
        )}
      </Paper >
    );
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Redux wiring
// ─────────────────────────────────────────────────────────────────────────────

const mapStateToProps = (state) => ({
  targetList: state.target,
  regionList: state.region,
  stateList: state.states,
  zoneList: state.zone,
  districtList: state.district,
  talukaList: state.taluka,
  beatList: state.beatTarget,
  areaList: state.area,
  yearList: state.year,
});

const mapDispatchToProps = {
  getRegionNew,
  getStateByRegion,
  getZoneByState,
  getDistrictByZone,
  getTalukaByDistrict,
  getAreaByTalukaAndYear,
  getNotAssignBeat,
  getActiveYear,
  getMonthsFromYearId,
  getTargetByStakeHolderTypeIdAndYearId,
  showLoador,
  showNotification,
};

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(StockHolderTarget);